import { useEffect, useState, type FC } from 'react';
import { z } from 'zod';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, ArrowRightIcon, ListFilter, Plus, Rocket } from 'lucide-react';
import { Card, CardContent } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { Form } from '@ui/form.tsx';
import { defineStepper } from '@ui/stepper.tsx';
import { SegmentRuleRow } from './segment-rule-row.tsx';
import { SegmentMembersTable } from './segment-members-table.tsx';
import { useSegmentCriteria } from '@admin/hooks/use-segment-criteria.ts';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import {
  useApiAdminsegmentsIdPatch,
  useApiAdminsegmentspreviewPost,
  getApiAdminsegmentsGetCollectionQueryKey,
  getApiAdminsegmentsIdGetQueryKey
} from '@r/api/generated/client.ts';
import type {
  SegmentSegmentReadModel,
  SegmentRuleSegmentUpdateModel,
  SegmentRuleSegmentPreviewWriteModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import type { SegmentFormValues, SegmentRuleFormValue } from './segment-form-types.ts';
import { getChoiceValue } from './segment-choice-utils.ts';

const { StepperProvider, StepperNavigation, StepperStep, StepperTitle, StepperPanel } =
  defineStepper(
    { id: 'step-criteria', title: 'criteria' },
    { id: 'step-members', title: 'members' }
  );

interface SegmentCriteriaFormProps {
  segment: SegmentSegmentReadModel;
}

/**
 * Assistant en deux étapes pour définir les règles d'un segment (avec estimation en
 * direct du nombre de correspondances) puis consulter les utilisateurs correspondants.
 */
export const SegmentCriteriaForm: FC<SegmentCriteriaFormProps> = ({ segment }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const { criteria } = useSegmentCriteria();
  const [previewCount, setPreviewCount] = useState<number | undefined>();

  const getDefaultRule = () => {
    const firstCriterion = criteria[0];

    return {
      field: firstCriterion?.field ?? '',
      operator: firstCriterion?.operators[0] ?? '',
      value:
        firstCriterion?.type === 'choice' && firstCriterion.choices?.[0]
          ? getChoiceValue(firstCriterion.choices[0])
          : ''
    };
  };

  const FormSchema = z.object({
    rules: z.array(
      z.object({
        field: z.string().min(1, t('form.error.required')),
        operator: z.string().min(1, t('form.error.required')),
        value: z.string().min(1, t('form.error.required'))
      })
    )
  });

  const form = useForm<SegmentFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      rules: (segment.rules ?? []).map((rule) => ({
        field: rule.field,
        operator: rule.operator,
        value: rule.value
      }))
    }
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'rules' });

  const goBackToList = () => navigate({ to: '/settings/segments' });

  const handleFinish = () => {
    toast.success(t('admin.segments.update.success'));
    goBackToList();
  };

  const updateMutation = useApiAdminsegmentsIdPatch({
    mutation: {
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'patch', '/admin/segments')) {
          showOrvalErrorToast(error, t('admin.segments.update.error'));
        } else {
          toast.error(t('admin.segments.update.error'));
        }
      }
    }
  });

  const previewMutation = useApiAdminsegmentspreviewPost();

  const rulesWatch = useWatch({ control: form.control, name: 'rules' });
  const debouncedRulesJson = useDebounce(JSON.stringify(rulesWatch), 500);

  useEffect(() => {
    const rules = JSON.parse(debouncedRulesJson) as SegmentRuleFormValue[];
    const isComplete = rules.every((rule) => rule.field && rule.operator && rule.value);
    if (!isComplete) {
      setPreviewCount(0);
      return;
    }

    const payload = rules.map(
      (rule) =>
        ({
          field: rule.field,
          operator: rule.operator,
          value: rule.value
        }) as SegmentRuleSegmentPreviewWriteModel
    );

    previewMutation.mutate(
      { data: { rules: payload } },
      { onSuccess: (result) => setPreviewCount(result.matchingCount) }
    );
  }, [debouncedRulesJson]);

  const onSubmit = async (data: SegmentFormValues, goToNextStep: () => void) => {
    const rules = data.rules.map(
      (rule) =>
        ({
          field: rule.field,
          operator: rule.operator,
          value: rule.value
        }) as SegmentRuleSegmentUpdateModel
    );

    try {
      const updatedSegment = await updateMutation.mutateAsync({
        id: String(segment.id),
        data: { rules }
      });

      queryClient.setQueryData(
        getApiAdminsegmentsIdGetQueryKey(String(segment.id)),
        updatedSegment
      );
      queryClient.invalidateQueries({ queryKey: getApiAdminsegmentsGetCollectionQueryKey() });
      goToNextStep();
    } catch {
      // L'erreur est déjà affichée par onError de la mutation ; on laisse l'utilisateur corriger et réessayer.
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="outline" rounded="full" onClick={goBackToList}>
        <ArrowLeftIcon className="mr-2 w-4 h-4" />
        {t('common.back')}
      </Button>

      <h1 className="text-4xl font-bold">{segment.name}</h1>

      <Form {...form} schema={FormSchema}>
        <StepperProvider variant="horizontal" labelOrientation="vertical" className="w-full">
          {({ methods }) => (
            <form
              onSubmit={form.handleSubmit((data) => onSubmit(data, methods.next))}
              className="space-y-6"
            >
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <h2 className="flex gap-2 items-center">
                      <ListFilter className="w-6 h-6" />
                      <p className="text-xl font-bold">{t('admin.segments.wizard.title')}</p>
                    </h2>
                    <p className="text-muted-foreground">
                      {t('admin.segments.wizard.description')}
                    </p>
                  </div>

                  <StepperNavigation className="pt-2 mt-8 mb-8 border-t border-border">
                    <StepperStep of="step-criteria">
                      <StepperTitle>{t('admin.segments.wizard.step.criteria')}</StepperTitle>
                    </StepperStep>
                    <StepperStep of="step-members">
                      <StepperTitle>{t('admin.segments.wizard.step.members')}</StepperTitle>
                    </StepperStep>
                  </StepperNavigation>

                  {methods.switch({
                    'step-criteria': () => (
                      <StepperPanel className="space-y-6">
                        <div className="w-fit rounded-2xl border bg-white p-6 space-y-1">
                          <p className="text-xs font-semibold uppercase text-muted-foreground">
                            {t('admin.segments.members.estimation.title')}
                          </p>
                          <p className="flex items-baseline gap-2">
                            <span className="text-3xl font-bold">{previewCount ?? '—'}</span>
                            <span className="text-base text-muted-foreground">
                              {t('admin.segments.members.estimation.unit')}
                            </span>
                          </p>
                        </div>

                        <div className="space-y-4">
                          {fields.map((field, index) => (
                            <div key={field.id}>
                              <SegmentRuleRow
                                form={form}
                                index={index}
                                criteria={criteria}
                                onRemove={() => remove(index)}
                              />
                              {index < fields.length - 1 && (
                                <div className="flex justify-center py-2">
                                  <span className="rounded-full bg-foreground px-3 py-1 text-xs font-semibold text-background">
                                    {t('admin.segments.rules.and')}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => append(getDefaultRule())}
                          >
                            <Plus className="mr-2 w-4 h-4" />
                            {t('admin.segments.rules.add')}
                          </Button>
                        </div>
                      </StepperPanel>
                    ),
                    'step-members': () => (
                      <StepperPanel>
                        <SegmentMembersTable segmentId={segment.id} />
                      </StepperPanel>
                    )
                  })}
                </CardContent>
              </Card>

              <div className="flex justify-between">
                {methods.current.id === 'step-criteria' ? (
                  <Button type="button" variant="outline" rounded="full" onClick={goBackToList}>
                    {t('common.previous')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    rounded="full"
                    onClick={() => methods.prev()}
                  >
                    {t('common.previous')}
                  </Button>
                )}

                {methods.current.id === 'step-criteria' ? (
                  <Button type="submit" rounded="full" loading={updateMutation.isPending}>
                    {t('common.next')} <ArrowRightIcon className="ml-2 w-4 h-4" />
                  </Button>
                ) : (
                  <Button type="button" rounded="full" onClick={handleFinish}>
                    {t('admin.segments.wizard.create.cta')} <Rocket className="ml-2 w-4 h-4" />
                  </Button>
                )}
              </div>
            </form>
          )}
        </StepperProvider>
      </Form>
    </div>
  );
};
